import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Input } from '@/components/ui/input';
import { useTranslation } from '@/hooks/useTranslation';
import { RequiredLabel } from './RequiredLabel';

type DurationSegments = {
  hours: string;
  minutes: string;
  seconds: string;
};

type DurationInputProps = {
  name: string;
  label: string;
  value: string | null | undefined;
  onChange: (value: string | number) => void;
  required?: boolean;
  requiredMulti?: boolean;
};

const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));

const parseDuration = (rawValue: string | null | undefined): DurationSegments => {
  if (!rawValue) {
    return { hours: '', minutes: '', seconds: '' };
  }

  const [rawHours = '', rawMinutes = '', rawSeconds = ''] = rawValue.split(':');

  const sanitize = (segment: string, max: number) => {
    if (!segment) return '';
    const numeric = Number(segment);
    if (Number.isNaN(numeric)) return '';
    return clamp(numeric, 0, max).toString();
  };

  return {
    hours: sanitize(rawHours, 999),
    minutes: sanitize(rawMinutes, 59),
    seconds: sanitize(rawSeconds, 59),
  };
};

const formatDuration = (segments: DurationSegments): string => {
  const { hours, minutes, seconds } = segments;
  const hasValue = [hours, minutes, seconds].some((segment) => segment !== '');
  if (!hasValue) {
    return '';
  }

  const pad = (segment: string, length: number) => segment.padStart(length, '0');
  const paddedHours = pad(hours === '' ? '0' : hours, 2);
  const paddedMinutes = pad(minutes === '' ? '0' : minutes, 2);
  const paddedSeconds = pad(seconds === '' ? '0' : seconds, 2);
  return `${paddedHours}:${paddedMinutes}:${paddedSeconds}`;
};

export const DurationInput = forwardRef(function DurationInput(
  { name, label, value, onChange, required = false, requiredMulti = false }: DurationInputProps,
  ref: React.Ref<{
    validate: () => boolean;
    inputRef?: React.RefObject<HTMLInputElement | null>;
  }>
) {
  const { t } = useTranslation();
  const [segments, setSegments] = useState<DurationSegments>(() => parseDuration(value));
  const segmentsRef = useRef<DurationSegments>(parseDuration(value));
  const pendingEmitRef = useRef<DurationSegments | null>(null);

  const hoursRef = useRef<HTMLInputElement | null>(null);
  const minutesRef = useRef<HTMLInputElement | null>(null);
  const secondsRef = useRef<HTMLInputElement | null>(null);
  const hiddenInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    const next = parseDuration(value);
    const current = segmentsRef.current;
    if (
      current.hours === next.hours &&
      current.minutes === next.minutes &&
      current.seconds === next.seconds
    ) {
      return;
    }
    segmentsRef.current = next;
    setSegments(next);
  }, [value]);

  const unitConfig = useMemo(
    () => ({
      hours: {
        short: t('traceability.generalTab.duration.hoursShort'),
        label: t('traceability.generalTab.duration.hoursLabel'),
      },
      minutes: {
        short: t('traceability.generalTab.duration.minutesShort'),
        label: t('traceability.generalTab.duration.minutesLabel'),
      },
      seconds: {
        short: t('traceability.generalTab.duration.secondsShort'),
        label: t('traceability.generalTab.duration.secondsLabel'),
      },
    }),
    [t]
  );

  const emitChange = useCallback(
    (nextSegments: DurationSegments) => {
      const formatted = formatDuration(nextSegments);
      if (hiddenInputRef.current) {
        hiddenInputRef.current.value = formatted;
      }
      onChange(formatted);
    },
    [onChange]
  );

  const emitChangeRef = useRef(emitChange);
  useEffect(() => {
    emitChangeRef.current = emitChange;
  });

  useEffect(() => {
    if (pendingEmitRef.current !== null) {
      emitChangeRef.current(pendingEmitRef.current);
      pendingEmitRef.current = null;
    }
  }, [segments]);

  const updateSegments = useCallback((updates: Partial<DurationSegments>) => {
    const next = { ...segmentsRef.current, ...updates };
    segmentsRef.current = next;
    pendingEmitRef.current = next;
    setSegments(next);
  }, []);

  const handleInputChange = useCallback(
    (key: keyof DurationSegments, max: number) => (event: React.ChangeEvent<HTMLInputElement>) => {
      const raw = event.target.value;
      if (raw === '') {
        updateSegments({ [key]: '' });
        return;
      }

      const numeric = Number(raw);
      if (Number.isNaN(numeric)) {
        return;
      }

      const clamped = clamp(numeric, 0, max).toString();
      updateSegments({ [key]: clamped });
    },
    [updateSegments]
  );

  const renderSegment = (
    key: keyof DurationSegments,
    options: { max: number; ref: React.RefObject<HTMLInputElement | null> }
  ) => {
    const unit = unitConfig[key];
    return (
      <div className="flex items-center gap-1.5" key={key}>
        <Input
          ref={options.ref}
          id={`${name}-${key}`}
          name={`${name}-${key}`}
          type="number"
          value={segments[key]}
          onChange={handleInputChange(key, options.max)}
          min={0}
          max={options.max}
          step={1}
          inputMode="numeric"
          aria-label={`${label} – ${unit.label}`}
          className="h-9 w-14 text-center [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
          data-required-multi={requiredMulti || undefined}
        />
        <span className="text-xs font-medium text-muted-foreground">{unit.short}</span>
      </div>
    );
  };

  useImperativeHandle(ref, () => ({
    validate: () => {
      const inputs = [hoursRef.current, minutesRef.current, secondsRef.current];
      for (const input of inputs) {
        if (!input) continue;
        if (input.value === '') continue;
        if (!input.checkValidity()) {
          input.reportValidity();
          return false;
        }
      }
      return true;
    },
    inputRef: hiddenInputRef,
  }));

  return (
    <div className="space-y-2">
      <RequiredLabel
        htmlFor={name}
        label={label}
        required={required}
        requiredMulti={requiredMulti}
      />

      <div className="flex flex-wrap items-center gap-2">
        {renderSegment('hours', { max: 999, ref: hoursRef })}
        <span className="text-sm font-semibold text-muted-foreground">:</span>
        {renderSegment('minutes', { max: 59, ref: minutesRef })}
        <span className="text-sm font-semibold text-muted-foreground">:</span>
        {renderSegment('seconds', { max: 59, ref: secondsRef })}
      </div>

      <input
        ref={hiddenInputRef}
        type="text"
        name={name}
        id={name}
        value={formatDuration(segments)}
        readOnly
        className="sr-only"
        required={required}
        data-required-multi={requiredMulti || undefined}
        aria-hidden="true"
      />
    </div>
  );
});
